import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  IconButton,
  DialogTitle,
  DialogActions,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Check as CheckIcon,
  AddPhotoAlternate as AddPhotoIcon,
  DeleteOutline as DeleteOutlineIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { Theme, ThemeFormData, ImageFormData, Image } from '../types';
import ThemeService from '../services/themeService';
import ImageService from '../services/imageService';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/apiConfig';
import { fetchWithAuth } from '../utils/authSession';
import { normalizeImageUrl } from '../utils/imageUtils';
import { chaaviGoldAddImageButtonSx } from './ChaaviVendorDashboardShell';

/** Aligned with BusinessManagementForm / edit modals */
const TM = {
  ink: '#1a1a1a',
  sub: '#888888',
  label: '#999999',
  required: '#e24b4a',
  border: '#dddddd',
  borderSoft: '#e4e4e4',
  rule: '#f0f0f0',
  fieldBg: '#fafafa',
  green: '#4a7c59',
  greenHover: '#3d6b4b',
  greenMuted: '#f5f9f6',
  greenTint: '#f0f7f0',
  greenFocusRing: 'rgba(74,124,89,0.1)',
  hint: '#aaaaaa',
  cancelColor: '#666666',
} as const;

const inputPropsSx = {
  fontSize: 14,
  color: TM.ink,
  '&::placeholder': { color: '#aaa', opacity: 1 },
};

const outlinedFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    bgcolor: TM.fieldBg,
    minHeight: 40,
    transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
    '& fieldset': { borderWidth: '0.5px', borderColor: TM.border },
    '&:hover fieldset': { borderColor: '#cccccc' },
    '&.Mui-focused': {
      bgcolor: '#ffffff',
      boxShadow: `0 0 0 3px ${TM.greenFocusRing}`,
    },
    '&.Mui-focused fieldset': { borderWidth: '0.5px', borderColor: TM.green },
    '&.Mui-error fieldset': { borderColor: TM.required },
  },
  '& .MuiOutlinedInput-input': { py: '10px', px: '12px', height: 'auto' },
  '& .MuiOutlinedInput-inputMultiline': { py: '10px', minHeight: 90, lineHeight: 1.5 },
};

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Typography
      component="label"
      sx={{
        fontSize: 11,
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: TM.label,
        display: 'block',
        mb: '5px',
        '& .req': { color: TM.required, ml: '2px' },
      }}
    >
      {children}
      {required ? <Box component="span" className="req">*</Box> : null}
    </Typography>
  );
}

function SectionRule() {
  return <Box sx={{ height: '0.5px', bgcolor: TM.rule, my: 0.5 }} />;
}

interface ThemeManagementFormProps {
  open: boolean;
  onClose: () => void;
  theme: Theme | null;
  businessId: string;
  onSuccess: (theme: Theme) => void;
}

type StagedUpload = { file: File; preview: string };

function extractNumericPrice(priceRange: string): number {
  if (!priceRange || priceRange === 'Contact for Quote') return 0;
  const match = priceRange.match(/₹?([\d,]+)/);
  if (match) {
    return parseFloat(match[1].replace(/,/g, '')) || 0;
  }
  return 0;
}

const ThemeManagementForm: React.FC<ThemeManagementFormProps> = ({
  open,
  onClose,
  theme,
  businessId,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<ThemeFormData>({
    businessId: businessId,
    themeName: '',
    themeDescription: '',
    themeCategory: '',
    priceRange: '',
    quantity: 0,
  });
  const [priceInput, setPriceInput] = useState<string>('');
  const [quantityInput, setQuantityInput] = useState<string>('0');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stagedUploads, setStagedUploads] = useState<StagedUpload[]>([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [existingImages, setExistingImages] = useState<Image[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<Image | null>(null);
  const { user } = useAuth();

  const themeCategories = [
    'Wedding',
    'Corporate',
    'Birthday',
    'Anniversary',
    'Graduation',
    'Holiday',
    'Photography',
    'Catering',
    'Decoration',
    'Entertainment',
    'Other',
  ];

  const revokeStagedPreviews = useCallback((items: StagedUpload[]) => {
    items.forEach((s) => URL.revokeObjectURL(s.preview));
  }, []);

  const fetchExistingImages = useCallback(async (themeId: string) => {
    try {
      setImagesLoading(true);
      const list = await ImageService.getImagesByThemeId(themeId);
      setExistingImages(list);
    } catch (e) {
      console.error(e);
      setExistingImages([]);
    } finally {
      setImagesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    if (theme) {
      setFormData({
        businessId: theme.businessId,
        themeName: theme.themeName,
        themeDescription: theme.themeDescription,
        themeCategory: theme.themeCategory,
        priceRange: theme.priceRange,
        quantity: theme.quantity ?? 0,
      });
      const numericPrice = extractNumericPrice(theme.priceRange);
      setPriceInput(numericPrice.toString());
      setQuantityInput((theme.quantity ?? 0).toString());
      void fetchExistingImages(theme.themeId);
    } else {
      setFormData({
        businessId: businessId,
        themeName: '',
        themeDescription: '',
        themeCategory: '',
        priceRange: '',
        quantity: 0,
      });
      setPriceInput('');
      setQuantityInput('0');
      setExistingImages([]);
    }
    setError(null);
  }, [theme, businessId, open, fetchExistingImages]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePriceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPriceInput(value);
    const numericValue = parseFloat(value) || 0;
    const priceRange = numericValue === 0 ? 'Contact for Quote' : `₹${numericValue.toLocaleString('en-IN')}`;
    setFormData((prev) => ({ ...prev, priceRange }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuantityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuantityInput(value);
    const quantity = parseInt(value, 10) || 0;
    setFormData((prev) => ({ ...prev, quantity }));
  };

  const handleAddStagedFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files || []);
    const valid: File[] = [];
    for (const file of picked) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 5 * 1024 * 1024) {
        setError('Each image must be 5MB or smaller.');
        continue;
      }
      valid.push(file);
    }
    if (valid.length === 0 && picked.length > 0) {
      setError('Only image files up to 5MB are allowed.');
    }
    if (valid.length > 0) {
      setError(null);
      setStagedUploads((prev) => {
        const next = [...prev, ...valid.map((file) => ({ file, preview: URL.createObjectURL(file) }))];
        return next;
      });
    }
    event.target.value = '';
  };

  const removeStagedAt = (index: number) => {
    setStagedUploads((prev) => {
      const item = prev[index];
      if (item) URL.revokeObjectURL(item.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadFilesToTheme = async (targetThemeId: string, files: File[], currentExisting: Image[]) => {
    const needsNewPrimary = !currentExisting.some((i) => i.isPrimary);

    const token = localStorage.getItem('token');
    const uploadPromises = files.map(async (file, index) => {
      const isPrimary = needsNewPrimary && index === 0;

      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'themes');
      fd.append('itemId', targetThemeId);

      const uploadResponse = await fetchWithAuth(`${API_BASE_URL}/api/files/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Vendor-Phone': user?.phoneNumber || '',
        },
        body: fd,
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({ message: 'Upload failed' }));
        throw new Error(errorData.message || `Upload failed: ${uploadResponse.status}`);
      }

      const uploadResult = await uploadResponse.json();
      const imageUrl = uploadResult.filePath.startsWith('http')
        ? uploadResult.filePath
        : `${API_BASE_URL}${uploadResult.filePath}`;

      const imageData: ImageFormData = {
        themeId: targetThemeId,
        imageName: file.name,
        imageUrl,
        imagePath: uploadResult.filePath,
        imageSize: file.size,
        imageType: file.type,
        isPrimary,
      };

      return ImageService.createImage(imageData, user?.phoneNumber);
    });

    await Promise.all(uploadPromises);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.themeName || !formData.themeDescription || !formData.themeCategory || !formData.priceRange) {
      setError('Please fill in all required fields');
      return;
    }

    const stagedFiles = stagedUploads.map((s) => s.file);
    const totalImagesAfter = existingImages.length + stagedFiles.length;

    if (!theme && stagedFiles.length === 0) {
      setError('Please add at least one image for the theme');
      return;
    }

    if (theme && totalImagesAfter === 0) {
      setError('A theme must have at least one image. Add images or undo deletions.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let updatedTheme: Theme;

      if (theme) {
        updatedTheme = await ThemeService.updateTheme(theme.themeId, formData, user?.phoneNumber);
      } else {
        updatedTheme = await ThemeService.createTheme(formData, user?.phoneNumber);
      }

      const targetId = updatedTheme.themeId;

      if (stagedFiles.length > 0) {
        setImageUploading(true);
        try {
          const existingForPrimary =
            theme != null ? existingImages : await ImageService.getImagesByThemeId(targetId);
          await uploadFilesToTheme(targetId, stagedFiles, existingForPrimary);
        } catch (imageErr: any) {
          console.error(imageErr);
          setError(`Theme saved, but images failed: ${imageErr.message || 'Unknown error'}`);
          setImageUploading(false);
          setLoading(false);
          onSuccess(updatedTheme);
          revokeStagedPreviews(stagedUploads);
          setStagedUploads([]);
          onClose();
          return;
        } finally {
          setImageUploading(false);
        }
      }

      revokeStagedPreviews(stagedUploads);
      setStagedUploads([]);
      onSuccess(updatedTheme);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save theme');
      console.error('Error saving theme:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDeleteImage = async () => {
    if (!imageToDelete || !user?.phoneNumber) return;
    try {
      await ImageService.deleteImage(imageToDelete.imageId, user.phoneNumber);
      setExistingImages((prev) => prev.filter((i) => i.imageId !== imageToDelete.imageId));
      setImageToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete image');
    }
  };

  const handleSetPrimary = async (imageId: string) => {
    try {
      await ImageService.setPrimaryImage(imageId, user?.phoneNumber);
      setExistingImages((prev) =>
        prev.map((img) => ({ ...img, isPrimary: img.imageId === imageId }))
      );
    } catch (err: any) {
      setError(err.message || 'Failed to set primary image');
    }
  };

  const handleClose = () => {
    revokeStagedPreviews(stagedUploads);
    setStagedUploads([]);
    setImageUploading(false);
    setPriceInput('');
    setQuantityInput('0');
    setError(null);
    setExistingImages([]);
    setImageToDelete(null);
    onClose();
  };

  const isEdit = Boolean(theme);
  const FORM_ID = 'theme-management-form';
  const stagedFiles = stagedUploads.map((s) => s.file);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.5)' } }}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 720,
            borderRadius: '16px',
            border: `0.5px solid ${TM.borderSoft}`,
            overflow: 'hidden',
            boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {error && (
            <Alert severity="error" sx={{ m: 2, mb: 0, borderRadius: '8px' }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ px: 3.5, pt: 3, pb: 0, borderBottom: `0.5px solid ${TM.rule}` }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    bgcolor: TM.greenTint,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.1,
                  }}
                >
                  <PaletteIcon sx={{ fontSize: 20, color: TM.green }} />
                </Box>
                <Typography sx={{ fontSize: 17, fontWeight: 500, color: TM.ink, m: 0, mb: '3px' }}>
                  {isEdit ? 'Edit theme' : 'Add new theme'}
                </Typography>
                <Typography sx={{ fontSize: 13, color: TM.sub, m: 0, mb: 2.5 }}>
                  {isEdit
                    ? 'Update details and manage gallery images for this theme.'
                    : 'Create a theme and add photos clients will see in Explore.'}
                </Typography>
              </Box>
              {isEdit && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: '#eaf3ec',
                    color: '#3d6b4b',
                    fontSize: 11,
                    fontWeight: 500,
                    px: 1,
                    py: 0.375,
                    borderRadius: '20px',
                    flexShrink: 0,
                    mt: 0.5,
                  }}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: TM.green }} />
                  Active
                </Box>
              )}
            </Box>
          </Box>

          <Box
            component="form"
            id={FORM_ID}
            onSubmit={handleSubmit}
            sx={{ px: 3.5, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.5,
              }}
            >
              <Box>
                <FieldLabel required>Theme name</FieldLabel>
                <TextField
                  required
                  fullWidth
                  name="themeName"
                  value={formData.themeName}
                  onChange={handleInputChange}
                  placeholder="e.g. Rustic wedding, Garden party"
                  hiddenLabel
                  variant="outlined"
                  InputProps={{ sx: inputPropsSx }}
                  sx={outlinedFieldSx}
                />
              </Box>
              <Box>
                <FieldLabel required>Category</FieldLabel>
                <FormControl fullWidth required variant="outlined">
                  <Select
                    name="themeCategory"
                    value={formData.themeCategory}
                    onChange={handleSelectChange}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Category' }}
                    renderValue={(v) => (v ? String(v) : '')}
                    sx={{
                      borderRadius: '8px',
                      bgcolor: TM.fieldBg,
                      minHeight: 40,
                      fontSize: 14,
                      '& .MuiOutlinedInput-notchedOutline': { borderWidth: '0.5px', borderColor: TM.border },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cccccc' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: '0.5px', borderColor: TM.green },
                      '&.Mui-focused': { bgcolor: '#fff', boxShadow: `0 0 0 3px ${TM.greenFocusRing}` },
                    }}
                  >
                    {themeCategories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box>
              <FieldLabel required>Description</FieldLabel>
              <TextField
                required
                fullWidth
                multiline
                rows={4}
                name="themeDescription"
                value={formData.themeDescription}
                onChange={handleInputChange}
                placeholder="What's included, style, ideal events…"
                hiddenLabel
                variant="outlined"
                InputProps={{ sx: inputPropsSx }}
                sx={outlinedFieldSx}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <Box>
                <FieldLabel required>Price (₹)</FieldLabel>
                <TextField
                  required
                  fullWidth
                  name="price"
                  type="number"
                  value={priceInput}
                  onChange={handlePriceInputChange}
                  placeholder="0"
                  hiddenLabel
                  variant="outlined"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{ sx: inputPropsSx }}
                  sx={outlinedFieldSx}
                />
                <Typography sx={{ fontSize: 11, color: TM.hint, mt: '1px' }}>{formData.priceRange}</Typography>
              </Box>
              <Box>
                <FieldLabel>Quantity (stock)</FieldLabel>
                <TextField
                  fullWidth
                  type="number"
                  name="quantity"
                  value={quantityInput}
                  onChange={handleQuantityInputChange}
                  placeholder="0"
                  hiddenLabel
                  variant="outlined"
                  inputProps={{ min: 0, step: 1 }}
                  InputProps={{ sx: inputPropsSx }}
                  sx={outlinedFieldSx}
                />
                <Typography sx={{ fontSize: 11, color: TM.hint, mt: '1px' }}>Units available</Typography>
              </Box>
            </Box>

            <SectionRule />

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <FieldLabel required={!isEdit}>Images</FieldLabel>
                  <Typography sx={{ fontSize: 12, color: TM.sub, mt: 0.25 }}>
                    {!isEdit ? 'At least one image required.' : 'Add, remove, or set a primary image.'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<AddPhotoIcon sx={{ fontSize: 18, color: '#fff' }} />}
                  sx={{
                    height: 38,
                    borderRadius: '8px',
                    fontSize: 13,
                    fontWeight: 500,
                    ...chaaviGoldAddImageButtonSx,
                  }}
                >
                  Add images
                  <input type="file" hidden multiple accept="image/*" onChange={handleAddStagedFiles} />
                </Button>
              </Box>

              {isEdit && imagesLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={28} sx={{ color: TM.green }} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mt: 1.5 }}>
                  {existingImages.map((img) => (
                    <Box
                      key={img.imageId}
                      sx={{
                        position: 'relative',
                        width: 96,
                        height: 96,
                        borderRadius: '10px',
                        overflow: 'hidden',
                        border: `0.5px solid ${TM.border}`,
                        bgcolor: TM.fieldBg,
                      }}
                    >
                      <Box
                        component="img"
                        src={normalizeImageUrl(img.imageUrl)}
                        alt={img.imageName}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {img.isPrimary && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            color: '#fff',
                            bgcolor: TM.green,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: '4px',
                          }}
                        >
                          Primary
                        </Box>
                      )}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          display: 'flex',
                          justifyContent: 'space-between',
                          background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
                          p: 0.25,
                        }}
                      >
                        <IconButton
                          type="button"
                          size="small"
                          onClick={() => void handleSetPrimary(img.imageId)}
                          sx={{ color: '#fff', p: 0.25 }}
                          title={img.isPrimary ? 'Primary' : 'Set as primary'}
                        >
                          {img.isPrimary ? <StarIcon sx={{ fontSize: 18 }} /> : <StarBorderIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                        <IconButton
                          type="button"
                          size="small"
                          onClick={() => setImageToDelete(img)}
                          sx={{ color: '#ffcdd2', p: 0.25 }}
                          title="Remove image"
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}

                  {stagedUploads.map((s, index) => (
                    <Box
                      key={`staged-${index}-${s.file.name}`}
                      sx={{
                        position: 'relative',
                        width: 96,
                        height: 96,
                        borderRadius: '10px',
                        overflow: 'hidden',
                        border: `0.5px dashed ${TM.green}`,
                        bgcolor: TM.greenMuted,
                      }}
                    >
                      <Box component="img" src={s.preview} alt={s.file.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          fontSize: 10,
                          fontWeight: 600,
                          color: TM.green,
                          bgcolor: '#fff',
                          px: 0.5,
                          borderRadius: '4px',
                        }}
                      >
                        New
                      </Box>
                      <IconButton
                        type="button"
                        size="small"
                        onClick={() => removeStagedAt(index)}
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          bgcolor: 'rgba(255,255,255,0.9)',
                          width: 28,
                          height: 28,
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16, color: TM.required }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              {stagedFiles.length > 0 && (
                <Typography sx={{ fontSize: 11, color: TM.hint, mt: 1 }}>
                  {stagedFiles.length} new file{stagedFiles.length === 1 ? '' : 's'} will upload when you save.
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              px: 3.5,
              py: 2,
              pb: 3,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button
              type="button"
              onClick={handleClose}
              disabled={loading}
              sx={{
                height: 38,
                px: 2.25,
                borderRadius: '8px',
                border: `0.5px solid ${TM.border}`,
                color: TM.cancelColor,
                textTransform: 'none',
                fontSize: 14,
                bgcolor: 'transparent',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              variant="contained"
              disabled={loading || imageUploading}
              startIcon={loading || imageUploading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CheckIcon sx={{ fontSize: 14 }} />}
              sx={{
                height: 38,
                px: 2.75,
                borderRadius: '8px',
                bgcolor: TM.green,
                textTransform: 'none',
                fontSize: 14,
                fontWeight: 500,
                boxShadow: 'none',
                '&:hover': { bgcolor: TM.greenHover, boxShadow: 'none' },
                '&:active': { transform: 'scale(0.98)' },
              }}
            >
              {loading || imageUploading
                ? imageUploading
                  ? 'Uploading…'
                  : 'Saving…'
                : isEdit
                  ? 'Update theme'
                  : 'Add theme'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(imageToDelete)} onClose={() => setImageToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>Remove image?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Delete “{imageToDelete?.imageName}”? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setImageToDelete(null)} color="inherit">
            Cancel
          </Button>
          <Button onClick={() => void handleConfirmDeleteImage()} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ThemeManagementForm;
